import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { clevertapEvent } from 'utils/clevertap';

// constants
type OrderType = 'Buy' | 'Sell';

const EVENTS_NAMES = {
  bondsPageViewed: 'FR Bonds Page Viewed',
  bondProductPageViewed: 'FR Bonds Product Page Viewed',
  bondOrderPageViewed: 'FR Bonds Order Page Viewed',
  bondPreviewOrderViewed: 'FR Bonds Preview Order Viewed',
  bondOrderSuccessViewed: 'FR Bonds Order Success Viewed',
  bondsAction: 'FR Bonds Action',
};

const PAGE_NAMES: Record<string, string> = {
  '/stream': 'Stream Page',
  '/symbol': 'Product Page',
  '/securities': 'Portofolio List',
  '/watchlist': 'Watchlist Page',
  '/screener': 'Screener Page',
  '/valuation': 'Valuation Page',
  '/sector': 'Sector Page',
  '/community': 'Community Page',
};

export default function useBondsAnalytics() {
  const router = useRouter();

  // fill the search related page
  const defaultPageSource = useMemo(() => {
    const pageNames = Object.keys(PAGE_NAMES);
    const pageIndex = pageNames.find((path) =>
      router.pathname.startsWith(path) ? path : undefined,
    );

    if (pageIndex) {
      return PAGE_NAMES[pageIndex];
    }

    return 'Search Page';
  }, [router.pathname]);

  const FRBondsNavigateTrackers = {
    // When user landing on FR Bonds page
    onBondPageViewed(section: string) {
      clevertapEvent(EVENTS_NAMES.bondsPageViewed, {
        Section: section,
      });
    },
    // When user landing on FR Bonds Product page
    onBondProductPageViewed(symbol: string, yieldValue: string) {
      clevertapEvent(EVENTS_NAMES.bondProductPageViewed, {
        Symbol: symbol,
        Yield: yieldValue,
      });
    },
    // When user landing on FR Bonds Order page
    onBondOrderPageViewed(
      orderType: OrderType,
      symbol: string,
      sourcePage?: string,
      yieldValue = '-',
    ) {
      clevertapEvent(EVENTS_NAMES.bondOrderPageViewed, {
        SourcePage: sourcePage || defaultPageSource,
        'Order Type': orderType,
        Symbol: symbol,
        Yield: yieldValue,
      });
    },
    // When user landing on FR Bonds Preview Order page
    onBondPreviewOrderViewed(
      orderType: OrderType,
      symbol: string,
      sourcePage?: string,
      yieldValue = '-',
    ) {
      clevertapEvent(EVENTS_NAMES.bondPreviewOrderViewed, {
        SourcePage: sourcePage || defaultPageSource,
        'Order Type': orderType,
        Symbol: symbol,
        Yield: yieldValue,
      });
    },
    // When user landing on FR Bonds Order Success page
    onBondOrderSuccessViewed(
      orderType: OrderType,
      symbol: string,
      sourcePage?: string,
      yieldValue = '-',
    ) {
      clevertapEvent(EVENTS_NAMES.bondOrderSuccessViewed, {
        SourcePage: sourcePage || defaultPageSource,
        'Order Type': orderType,
        Symbol: symbol,
        Yield: yieldValue,
      });
    },
  };

  const FRBondActionTrackers = {
    // When user click FR Bonds introduction banner
    onIntroductionBannerClicked(sourcePage: string) {
      clevertapEvent(EVENTS_NAMES.bondsAction, {
        Action: 'Introduction Banner Clicked',
        SourcePage: sourcePage || defaultPageSource,
      });
    },
    // When user click “Saya Setuju” on FR Bonds TnC
    onTnCConfirmed() {
      clevertapEvent(EVENTS_NAMES.bondsAction, {
        Action: 'TnC Confirmed',
      });
    },
    // When user click FR Bonds Section/Catalog to be redirected to FR Bonds list
    onFRProductListClicked(sourcePage: string) {
      clevertapEvent(EVENTS_NAMES.bondsAction, {
        Action: 'FR Product List Clicked',
        SourcePage: sourcePage || defaultPageSource,
      });
    },
    // When user click buy/sell button on FR product to initiate order
    onOrderButtonClicked(
      orderType: OrderType,
      symbol: string,
      sourcePage?: string,
      yieldValue = '-',
    ) {
      clevertapEvent(EVENTS_NAMES.bondsAction, {
        Action: 'FR Order Button Clicked',
        SourcePage: sourcePage || defaultPageSource,
        'Order Type': orderType,
        Symbol: symbol,
        Yield: yieldValue,
      });
    },
    // When user click buy/sell button on FR order page to finalize order
    onOrderPlaced(
      orderType: OrderType,
      symbol: string,
      sourcePage?: string,
      yieldValue = '-',
    ) {
      clevertapEvent(EVENTS_NAMES.bondsAction, {
        Action: 'FR Order Placed',
        SourcePage: sourcePage || defaultPageSource,
        'Order Type': orderType,
        Symbol: symbol,
        Yield: yieldValue,
      });
    },
    // When user click buy/sell button on FR order page, confirming the order
    onOrderConfirmed(
      orderType: OrderType,
      symbol: string,
      sourcePage?: string,
      yieldValue = '-',
    ) {
      clevertapEvent(EVENTS_NAMES.bondsAction, {
        Action: 'FR Order Confirmed',
        SourcePage: sourcePage || defaultPageSource,
        'Order Type': orderType,
        Symbol: symbol,
        Yield: yieldValue,
      });
    },
  };

  return {
    FRBondsNavigateTrackers,
    FRBondActionTrackers,
  };
}
